/*
 * Copyright (c) 2017 Datarista, Inc.
 */

import { Map, Record, List } from 'immutable';
import { Account } from './account';
import { actionTypes } from '../actions/data';

export const Layout = new Record(
  {
    id: 0,
    datasetId: 0,
    filename: '',
    mimeType: '',
    s3Key: '',
    status: 0,
    description: null,
    adminNotes: null,
    createdBy: 0,
    createdOn: '',
    updatedBy: 0,
    updatedOn: '',
  },
  'Layout'
);

export const DataSet = new Record(
  {
    id: null,
    index: 0,
    accountId: null,
    providerDataSetNum: '',
    name: '',
    listType: '',
    listGeo: '',
    status: 0,
    code: '',
    s3Path: '',
    searchHost: null,
    customType: null,
    incrementalUpdates: false,
    autoVersionUpdates: false,
    createdBy: 1,
    createdOn: '',
    updatedBy: 1,
    updatedOn: '',
    state: '',
  },
  'DataSet'
);

export const FieldSet = new Record(
  {
    id: 0,
    datasetId: 0,
    name: '',
    setType: 0,
    status: 0,
    createdBy: 0,
    createdOn: '',
    updatedBy: 0,
    updatedOn: '',
    fieldCount: 0,
    orderCount: 0,
    installedCount: 0,
  },
  'FieldSet'
);

export const Field = new Record(
  {
    id: 0,
    datasetId: 0,
    filesetId: 0,
    name: '',
    position: '',
    dataType: '',
    length: 0,
    format: '',
    description: '',
    standardFieldType: '',
    uiSelectionType: '',
    isPrimaryKey: false,
    parentFilesetField: 0,
    displayGroup: '',
    selectionGroup: '',
    isSearchable: false,
    isInternal: false,
    scale: 0,
    isSimpleSearch: false,
    isTagDescriptor: false,
    isSkuMandatory: false,
  },
  'Field'
);

export const FieldInSku = new Record(
  {
    id: 0,
    fieldsetId: 0,
    fieldId: 0,
    uiSelectionType: '',
    uiDisplay: 0,
    ordering: 0,
  },
  'FieldInSku'
);

export const SyncFields = new Record(
  {
    id: 0,
    fieldsetId: 0,
    fieldId: 0,
    ordering: 0,
  },
  'SyncFields'
);

export const Version = new Record(
  {
    id: 0,
    datasetId: 0,
    version: 0,
    status: 0,
    createdOn: '',
    updatedOn: '',
  },
  'Version'
);

export const Fileset = new Record(
  {
    id: 0,
    datasetId: 0,
    dataFilenamePattern: '',
    layoutFilename: '',
    recordLevelTypeId: 0,
    recordLevelType: '',
  },
  'Fileset'
);

export const FTPCredentials = new Record(
  {
    id: 0,
    accountId: 0,
    username: '',
    passwordHash: '',
    uid: 0,
    gid: 0,
    directory: '',
  },
  'ftpCredentials'
);

const data = new Map({
  accounts: new List(),
  dataSets: new Map(),
  dataSet: new DataSet(),
  fieldSets: new Map(),
  fieldSetsSummary: new Map(),
  dataSetsSummary: new Map(),
  filesetPreviewSummary: new Map(),
  recordsSummary: new Map(),
  versionsSummary: new Map(),
  layouts: new Map(),
  newLayout: new Layout(),
  newDataset: new DataSet(),
  currPage: 1,
  fields: new List(),
  ftpCredentials: new FTPCredentials(),
  syncFields: new SyncFields(),
  currDataset: 0,
  currDatasetName: '',
  currentFilesets: new List(),
  currentDatasetInfo: new DataSet(),
  isAdmin: false,
  currPlatformCode: 0,
  dragTableRowState: new Map(),
  dragTableCheckAll: false,
  dropTableRowState: new Map(),
  dropTableCheckAll: false,
  datasetForSkuIsPreSelected: false,
  isEdit: false,
  currentSkuFields: new List(),
  currentSkuId: 0,
  currentSkuInfo: new FieldSet(),
  currentGroupFields: new List(),
  dragTableChecked: new Map(),
  dropTableChecked: new Map(),
  dragTableComplete: new Map(),
  dropTableComplete: new Map(),
  skuIsInUse: true,
  isAdminPage: false,
});

const initialState = data;

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.DATASETS_FETCH_SUCCESS: {
      const dataSetRecords = new List(action.dataSets.map(dataSet => new DataSet(dataSet)));

      const currentDataSetsSummary = state.get('dataSetsSummary');
      const dataMap = currentDataSetsSummary.set(action.accountId, dataSetRecords);
      return state.merge({
        dataSets: dataSetRecords,
        dataSetsSummary: dataMap,
      });
    }
    case actionTypes.FETCH_SINGLE_DATASET_SUCCESS: {
      return state.merge({ dataset: new DataSet(action.dataset) });
    }
    case actionTypes.FIELDSETS_FETCH_SUCCESS: {
      const fieldSetRecords = action.fieldSets.map(fieldSet => new FieldSet(fieldSet));
      const currentFieldSetSummary = state.get('fieldSetsSummary');
      const fieldMap = currentFieldSetSummary.set(action.dataSetId, fieldSetRecords);
      return state.merge({
        fieldSets: fieldSetRecords,
        fieldSetsSummary: fieldMap,
      });
    }
    case actionTypes.FETCH_VERSIONS: {
      const versionRecords = action.versions.map(version => new Version(version));
      const currentVersionsSummary = state.get('versionsSummary');
      const versionsMap = currentVersionsSummary.set(action.datasetId.toString(), versionRecords);
      return state.merge({ versionsSummary: versionsMap });
    }
    case actionTypes.TOTAL_RECORDS_SUCCESS: {
      const currentrecordsSummary = state.get('recordsSummary');
      const filesetCountArr = new List(
        action.recordsInfo.map(
          filesetCountRecord =>
            new Map({
              fileSet: new Fileset(filesetCountRecord.fileSet),
              count: filesetCountRecord.count,
            })
        )
      );
      const recordsMap = currentrecordsSummary.set(action.datasetId, filesetCountArr);
      return state.merge({ recordsSummary: recordsMap });
    }
    case actionTypes.LAYOUTS_FETCH_BY_ID_SUCCESS: {
      const layoutRecords = action.layouts.map(layout => new Layout(layout));
      return state.merge({ layouts: layoutRecords });
    }
    case actionTypes.CREATE_DATASET_SUCCESS: {
      const dataSet = new DataSet(action.datasetInfo);
      const nextPage = action.currPage + 1;
      return state.merge({
        newDataset: dataSet,
        currPage: nextPage,
        currentDatasetInfo: dataSet,
      });
    }
    case actionTypes.NEXT_PAGE: {
      const nextPage = action.currPage + 1;
      return state.merge({
        currPage: nextPage,
        datasetForSkuIsPreSelected: false,
        isEdit: false,
        fields: new List(),
      });
    }
    case actionTypes.RESET_NEW_LIST_BOOLEANS: {
      return state.merge({
        currDataset: 0,
        datasetForSkuIsPreSelected: false,
        currDatasetName: '',
        isEdit: false,
        currentSkuId: 0,
        currentSkuInfo: new FieldSet(),
        currentSkuFields: new List(),
        dragTableComplete: new Map(),
        dropTableComplete: new Map(),
      });
    }
    case actionTypes.LIST_ALL_ACCOUNTS_SUCCESS: {
      const newAccounts = new List(action.accounts.map(account => new Account(account)));
      return state.merge({ accountsFetched: true, accounts: newAccounts });
    }
    case actionTypes.FIELD_LIST_SUCCESS: {
      const newFieldArray = action.fields.map(field => new Field(field));
      const newFieldsList = new List(newFieldArray);
      return state.merge({ fields: newFieldsList });
    }
    case actionTypes.GET_FIELDS_FOR_SKU: {
      return state.merge({ currentSkuFields: action.skuFields });
    }
    case actionTypes.NEW_SKU_SUCCESS: {
      return state.merge({ currentSkuInfo: new FieldSet() });
    }
    case actionTypes.FIELD_SYNC_SUCCESS: {
      const currentFieldSetSummary = state.get('fieldSetsSummary');
      const newFieldSetSummary = currentFieldSetSummary.delete(action.datasetId);
      const sync = new SyncFields(action.syncedFields);
      return state.merge({
        syncFields: sync,
        fieldSetsSummary: newFieldSetSummary,
        datasetForSkuIsPreSelected: false,
        fields: new List(),
      });
    }
    case actionTypes.OPEN_EDIT_LIST: {
      return state.merge({ currDataset: action.datasetId, isAdmin: action.admin });
    }
    case actionTypes.OPEN_CONFIG_FORM: {
      return state.merge({ currPlatformCode: action.platformCode, currDataset: action.datasetId });
    }
    case actionTypes.OPEN_SKU_CREATION: {
      return state.merge({
        currDataset: action.datasetId,
        datasetForSkuIsPreSelected: true,
        currPage: 3,
        currDatasetName: action.datasetName,
        isEdit: false,
      });
    }
    case actionTypes.OPEN_GROUP_CREATION: {
      return state.merge({
        currDataset: action.datasetId,
        datasetForSkuIsPreSelected: true,
        currPage: 4,
        currDatasetName: action.datasetName,
        isEdit: false,
      });
    }
    case actionTypes.DELETE_SKU: {
      let currentFieldSetsSummary = state.get('fieldSetsSummary');
      currentFieldSetsSummary = currentFieldSetsSummary.delete(action.fieldsetInfo.datasetId);
      return state.merge({ fieldSetsSummary: currentFieldSetsSummary });
    }
    case actionTypes.OPEN_EDIT_SKU: {
      const newFieldList = action.skuFields.map(field => new FieldInSku(field));
      // const currentSku = undefined; // new FieldSet(action.sku);
      const currentSku = new FieldSet(action.sku);
      return state.merge({
        currDataset: action.datasetId,
        datasetForSkuIsPreSelected: true,
        currPage: 3,
        currDatasetName: action.datasetName,
        isEdit: true,
        currentSkuId: action.fieldsetId,
        currentSkuInfo: currentSku,
        currentSkuFields: newFieldList,
        isAdminPage: action.isAdminPage,
      });
    }
    case actionTypes.FETCH_GROUP_FIELDS_SUCCESS: {
      const newFieldList = action.groupFields.map(field => new FieldInSku(field));
      return state.merge({ currentGroupFields: newFieldList });
    }
    case actionTypes.FETCH_FILESET_SUCCESS: {
      // make filesets retrievable by id
      const fieldsetsObj = {};
      action.filesets.forEach(fileset => {
        const filesetId = fileset.id;
        const filesetRecord = new Fileset(fileset);
        fieldsetsObj[filesetId] = filesetRecord;
      });
      const filesetMap = new Map(fieldsetsObj);
      return state.merge({ currentFilesets: filesetMap });
    }
    case actionTypes.OPEN_FILESET_PREVIEW: {
      const filesetList = new List(action.filesetInfo.map(fileset => new Fileset(fileset)));
      const dataset = new DataSet(action.dataset);
      return state.merge({ currentDatasetInfo: dataset, currentFilesets: filesetList });
    }
    case actionTypes.FETCH_FILESET_PREVIEW: {
      let currentFilesetSummary = state.get('filesetPreviewSummary');
      let currentFilesetPreview = new Map();
      currentFilesetPreview = currentFilesetPreview.set('fields', action.filesetPreviewInfo.fields);
      currentFilesetPreview = currentFilesetPreview.set(
        'records',
        action.filesetPreviewInfo.records
      );
      currentFilesetSummary = currentFilesetSummary.set(action.filesetId, currentFilesetPreview);
      return state.merge({ filesetPreviewSummary: currentFilesetSummary });
    }
    case actionTypes.SET_CURRENT_DATASET: {
      return state.merge({ currDataset: action.datasetId });
    }
    case actionTypes.UPDATE_DRAG_TABLE_ROW_STATE: {
      const currentRowState = state.get('dragTableRowState');
      const newRowState = currentRowState.set(action.id, action.value);
      return state.merge({ dragTableRowState: newRowState, dragTableCheckAll: false });
    }
    case actionTypes.CHECK_ALL_DRAG_TABLE_ROW_STATE: {
      return state.merge({ dragTableRowState: action.newMap, dragTableCheckAll: action.value });
    }
    case actionTypes.UPDATE_DRAG_TABLE_CHECKED: {
      return state.merge({ dragTableChecked: action.newMap });
    }
    case actionTypes.UPDATE_DROP_TABLE_COMPLETE: {
      return state.merge({ dropTableComplete: action.newMap });
    }
    case actionTypes.UPDATE_DROP_TABLE_CHECKED: {
      return state.merge({ dropTableChecked: action.newMap });
    }
    case actionTypes.UPDATE_DROP_TABLE_ROW_STATE: {
      const currentRowState = state.get('dropTableRowState');
      const newRowState = currentRowState.set(action.id, action.value);
      return state.merge({ dropTableRowState: newRowState, dropTableCheckAll: false });
    }
    case actionTypes.CHECK_ALL_DROP_TABLE_ROW_STATE: {
      return state.merge({ dropTableRowState: action.newMap, dropTableCheckAll: action.value });
    }
    case actionTypes.UPDATE_DRAG_TABLE_COMPLETE: {
      return state.merge({ dragTableComplete: action.newMap });
    }
    case actionTypes.CLEAR_FIELDS_FOR_SKU: {
      return state.merge({ currentSkuFields: new List(), fields: new List() });
    }
    case actionTypes.FTP_CREDENTIALS_FETCH_SUCCESS: {
      let currentFTPCredentials = { notSet: true }; // If false, set property component can check
      if (action.ftpCredentials) {
        currentFTPCredentials = new FTPCredentials(action.ftpCredentials);
      }
      return state.merge({ ftpCredentials: currentFTPCredentials });
    }
    default: {
      return state;
    }
  }
}
